import { Component } from '@theme/component';

/**
 * A custom element that manages a tab group rendered server-side via Liquid.
 *
 * `tab-group.liquid` captures all `_tab-pane` child output and splits it on
 * HTML comment markers to place buttons in `[role="tablist"]` and panels in
 * `.tab-group__panels`. This component is only responsible for:
 *   1. Activating the first tab on connect.
 *   2. Handling tab button clicks delegated via `on:click="/selectTab"`.
 *
 * @extends {Component}
 */
class TabGroupComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    const firstTab = this.querySelector('[role="tab"]');
    if (firstTab instanceof HTMLElement) this.#activate(firstTab);
  }

  /**
   * Called via `on:click="/selectTab"` on each tab button.
   *
   * @param {MouseEvent} event
   */
  selectTab = (event) => {
    if (!(event.target instanceof Element)) return;
    const btn = event.target.closest('[role="tab"]');
    if (btn instanceof HTMLElement) this.#activate(btn);
  };

  /**
   * Marks `activeBtn` as selected and shows its associated panel,
   * deactivating all others.
   *
   * @param {HTMLElement} activeBtn
   */
  #activate(activeBtn) {
    const panelId = activeBtn.getAttribute('aria-controls');

    for (const btn of this.querySelectorAll('[role="tab"]')) {
      const isActive = btn === activeBtn;
      btn.setAttribute('aria-selected', String(isActive));
      if (isActive) btn.setAttribute('data-active', '');
      else btn.removeAttribute('data-active');
    }

    for (const panel of this.querySelectorAll('.tab-pane')) {
      const isActive = panel.id === panelId;
      if (isActive) panel.removeAttribute('hidden');
      else panel.setAttribute('hidden', '');
    }
  }
}

if (!customElements.get('tab-group')) {
  customElements.define('tab-group', TabGroupComponent);
}
